// import external libraries
import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useDebounceValue } from 'usehooks-ts';
import { useShallow } from 'zustand/shallow';

// import hooks
import { useRosterIntervalDays } from 'components/shared/RosterViewDataGrid';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useBacklogActivityOptions, useGetBacklog } from './Backlog.queries';
import { useBacklogPreferencesStore } from './Backlog.stores';

// import utils
import { groupRosterDaysByWeek } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';
import {
  buildBacklogRows,
  buildDisplayRows,
  filterBacklogRows,
  getCategoryRowKey,
  getTaskCategoryCounts,
  getWeekBuckets
} from './components/BacklogGrid/BacklogGrid.utils';
import { missingRequiredFilter, toLegacyApiDateTime } from './Backlog.utils';
import { getEightWeekInterval } from 'utils/date';

// import constants
import {
  BACKLOG_CATEGORY_DEBOUNCE_MS,
  CATEGORIES_FOLDED_BY_DEFAULT,
  IMPLEMENTED_CATEGORIES
} from './Backlog.consts';

// import types
import type { SingleOrRange } from 'components/shared/DateNavigation/DateNavigation.types';
import type { BacklogFilterState, BacklogTaskCategory } from './Backlog.types';

export const useBacklog = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  // The category rows the planner has flipped away from their default fold state (see
  // CATEGORIES_FOLDED_BY_DEFAULT). Kept as "toggled" rather than "collapsed" so one set serves both a
  // default-open category (a toggle folds it) and a default-folded one (a toggle opens it).
  const [toggledFoldKeys, setToggledFoldKeys] = useState<Set<string>>(
    new Set()
  );
  // Every category that can actually report something starts selected.
  const [activeCategories, setActiveCategories] = useState<
    BacklogTaskCategory[]
  >(IMPLEMENTED_CATEGORIES);
  // Week mode is chosen from the date-picker's period menu; it shows the week-detail for the first week
  // of the window. Clicking an outside-treatment cell opens one specific week on top of that. That week
  // is held as its index in the window (0-based), not as a column field name: the index stays valid when
  // the window shifts, so paging the date keeps the detail on the adjacent week rather than snapping back
  // to the first — a field name would stop existing in the new window. Null means "no specific week", so
  // week mode falls back to the first week of the window.
  const [weekMode, setWeekMode] = useState(false);
  const [openWeekIndex, setOpenWeekIndex] = useState<number | null>(null);
  // The date-picker owns its period in this shared store; setting it keeps the picker's menu in step
  // when the view is opened from a cell rather than from the menu itself.
  const setDateNavigationPeriod = useDateNavigationStore(
    (state) => state.setPeriod
  );

  // The two filters and the three view settings are remembered across visits, so a planner does not
  // set them again each time the page opens. Their defaults live in the store: no narrowing filters,
  // the signed difference in the cells, and both the totals column and the fulfilled weeks shown.
  const {
    rowEntityType,
    entityFilters,
    employeeViewFilters,
    activityIds,
    cellValueMode,
    showTotals,
    showCompleted,
    setRowEntityType,
    setEntityFilters,
    setEmployeeViewFilters,
    setActivityIds,
    setCellValueMode,
    setShowTotals,
    setShowCompleted
  } = useBacklogPreferencesStore(
    useShallow((state) => ({
      rowEntityType: state.rowEntityType,
      entityFilters: state.entityFilters,
      employeeViewFilters: state.employeeViewFilters,
      activityIds: state.activityIds,
      cellValueMode: state.cellValueMode,
      showTotals: state.showTotals,
      showCompleted: state.showCompleted,
      setRowEntityType: state.setRowEntityType,
      setEntityFilters: state.setEntityFilters,
      setEmployeeViewFilters: state.setEmployeeViewFilters,
      setActivityIds: state.setActivityIds,
      setCellValueMode: state.setCellValueMode,
      setShowTotals: state.setShowTotals,
      setShowCompleted: state.setShowCompleted
    }))
  );

  // What the tiles ask the backend for, settled. The tiles themselves stay instant: `activeCategories`
  // still drives the rows and the tile styling without delay, so only the request waits.
  const [requestedCategories] = useDebounceValue(
    activeCategories,
    BACKLOG_CATEGORY_DEBOUNCE_MS
  );

  // The filter the view still needs before it asks anything — a care receiver in the patient view, a plan
  // unit in the employee view. The page turns this into an empty state naming what to pick, rather than
  // showing a blank grid with no reason given.
  const requiredFilter = missingRequiredFilter({
    rowEntityType,
    entityFilters,
    planUnitIds: employeeViewFilters.planUnitIds
  });

  const dateInterval = useMemo(
    () => getEightWeekInterval(selectedDate),
    [selectedDate]
  );

  // Every activity the tenant plans with, straight from the activities endpoint — not derived from this
  // page's own response, which used to cost a second backlog fetch just to keep the list complete.
  const { options: activityOptions, isLoading: isLoadingActivityOptions } =
    useBacklogActivityOptions();

  // The applied ids as options again, for the popover's draft and the badge. An id the tenant no longer
  // has an activity for simply drops out, so a remembered filter cannot name something that is gone.
  const selectedActivityOptions = useMemo(
    () => activityOptions.filter(({ id }) => activityIds.includes(id)),
    [activityOptions, activityIds]
  );

  // The request is made with the same ids the popover and the badge show, not the raw stored ones: an
  // activity the tenant no longer has would otherwise narrow the grid permanently while no field could
  // name it, and be dropped without a word the first time the planner pressed Toepassen.
  const appliedActivityIds = useMemo(
    () => selectedActivityOptions.map(({ id }) => id),
    [selectedActivityOptions]
  );

  // Which means the request waits for the activity list whenever there is a remembered filter to resolve.
  // Asking before it lands would fetch the whole period unnarrowed and then fetch it again — two requests
  // and a flash of rows the filter excludes, on a page whose point is to ask the backend for less.
  const isAwaitingActivityOptions =
    isLoadingActivityOptions && activityIds.length > 0;

  const {
    data: backlog,
    isLoading,
    isError,
    refetch
  } = useGetBacklog({
    startDate: dateInterval.startDate,
    endDate: dateInterval.endDate,
    // Deviating weeks only, unless the planner asks to see the fulfilled ones as well.
    includeAllSummaries: showCompleted,
    entityFilters,
    activityIds: appliedActivityIds,
    activeCategories: requestedCategories,
    rowEntityType,
    isRequestHeld: requiredFilter !== null || isAwaitingActivityOptions
  });

  const { rosterDays: intervalRosterDays } = useRosterIntervalDays({
    defaultInterval: dateInterval
  });

  // The rows are built here rather than in the grid so the page can tell "no data" apart from
  // "nothing left after filtering".
  const { rosterDays, weeks } = useMemo(() => {
    const groupedWeeks = groupRosterDaysByWeek(intervalRosterDays);
    return { rosterDays: groupedWeeks.flat(), weeks: groupedWeeks };
  }, [intervalRosterDays]);

  // Only what still narrows the rows here: the entity and activity picks are resolved by the backend, so
  // the response is already scoped to them by the time it reaches this.
  const filters: BacklogFilterState = useMemo(
    () => ({ activeCategories, showCompleted }),
    [activeCategories, showCompleted]
  );

  const allRows = useMemo(
    () => buildBacklogRows(backlog, weeks),
    [backlog, weeks]
  );

  // Counted over the server-scoped rows, before the tile filter, so each tile shows what selecting
  // it would bring in.
  const categoryCounts = useMemo(
    () => getTaskCategoryCounts(allRows),
    [allRows]
  );

  const ruleRows = useMemo(
    () => filterBacklogRows(allRows, filters),
    [allRows, filters]
  );

  // The actual collapsed set, derived from the planner's toggles and each category's default fold
  // state. A default-folded category (Tekort/Overschot) is collapsed unless it was opened; every other
  // category is collapsed only once it was folded. Deriving it — rather than seeding state per care
  // receiver — keeps new care receivers (after a period or filter change) folded too.
  const collapsedCategoryKeys = useMemo(() => {
    const foldedByDefault = new Set(CATEGORIES_FOLDED_BY_DEFAULT);
    const collapsed = new Set<string>();
    allRows.forEach((row) => {
      const key = getCategoryRowKey(row.careReceiverId, row.category);
      const toggled = toggledFoldKeys.has(key);
      if (foldedByDefault.has(row.category) ? !toggled : toggled) {
        collapsed.add(key);
      }
    });
    return collapsed;
  }, [allRows, toggledFoldKeys]);

  // Header rows are added after filtering, so a category whose every rule was filtered out does not
  // leave an empty header behind.
  const rows = useMemo(
    () => buildDisplayRows(ruleRows, weeks, collapsedCategoryKeys),
    [ruleRows, weeks, collapsedCategoryKeys]
  );

  const onDateChange = useCallback((date: SingleOrRange | null) => {
    if (!date) return;
    const raw = Array.isArray(date) ? date[0] : date;
    if (!raw) return;
    setSelectedDate(dayjs.isDayjs(raw) ? raw.toDate() : raw);
  }, []);

  const onToggleCategoryRow = useCallback(
    (careReceiverId: string, category: BacklogTaskCategory) => {
      const key = getCategoryRowKey(careReceiverId, category);
      setToggledFoldKeys((previous) => {
        const next = new Set(previous);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    []
  );

  const onToggleCategory = useCallback((category: BacklogTaskCategory) => {
    setActiveCategories((previous) =>
      previous.includes(category)
        ? previous.filter((active) => active !== category)
        : [...previous, category]
    );
  }, []);

  const onToggleAll = useCallback(() => {
    setActiveCategories((previous) =>
      IMPLEMENTED_CATEGORIES.every((category) => previous.includes(category))
        ? []
        : IMPLEMENTED_CATEGORIES
    );
  }, []);

  // Clicking a cell opens that specific week and enters week mode, so paging the date afterwards stays
  // in the week-detail (on the adjacent week) rather than dropping back to the grid. The clicked week is
  // stored as its index in the window, so it survives a window shift. It also sets the date-picker's own
  // period, so its menu reflects "week" the way choosing it from there would.
  const onOpenWeek = useCallback(
    (weekField: string) => {
      const index = getWeekBuckets(weeks).findIndex(
        ({ field }) => field === weekField
      );
      setOpenWeekIndex(index === -1 ? 0 : index);
      setWeekMode(true);
      setDateNavigationPeriod('week');
    },
    [weeks, setDateNavigationPeriod]
  );

  const onCloseWeek = useCallback(() => {
    setOpenWeekIndex(null);
    setWeekMode(false);
    setDateNavigationPeriod('eightWeeks');
  }, [setDateNavigationPeriod]);

  // The date-picker's period menu drives the view: 'week' opens the week-detail, 'eightWeeks' (or any
  // other) returns to the grid. Stable so the DateNavigation effect that reports the period does not
  // loop. A clicked week survives the 'week' report (it also sets the period), so only leaving week mode
  // clears it.
  const onPeriodChange = useCallback((period: string) => {
    setWeekMode(period === 'week');
    if (period !== 'week') setOpenWeekIndex(null);
  }, []);

  // The week actually shown: in week mode, the clicked week (by its window index) or the first week when
  // none was clicked. The index is resolved against the current window, so a window shift moves it to
  // the adjacent week instead of snapping back — no reset needed when the window changes.
  const openWeek = useMemo(() => {
    if (!weekMode) return null;
    const buckets = getWeekBuckets(weeks);
    return buckets[openWeekIndex ?? 0]?.field ?? buckets[0]?.field ?? null;
  }, [openWeekIndex, weekMode, weeks]);

  return {
    selectedDate,
    onDateChange,
    activityOptions,
    selectedActivityOptions,
    onActivityIdsChange: setActivityIds,
    rowEntityType,
    onRowEntityTypeChange: setRowEntityType,
    requiredFilter,
    employeeViewFilters,
    onEmployeeViewFiltersChange: setEmployeeViewFilters,
    // The window the grid is showing, for the filter lists that are asked per period. A full datetime, not
    // a date — see toLegacyApiDateTime.
    startDate: toLegacyApiDateTime(dateInterval.startDate),
    endDate: toLegacyApiDateTime(dateInterval.endDate),
    entityFilters,
    onEntityFiltersChange: setEntityFilters,
    categoryCounts,
    activeCategories,
    onToggleCategory,
    onToggleAll,
    onToggleCategoryRow,
    cellValueMode,
    setCellValueMode,
    showTotals,
    setShowTotals,
    showCompleted,
    setShowCompleted,
    rosterDays,
    rows,
    // The week-detail reuses the same filtered data as the weeks grid: the raw response (for events),
    // the filtered rule rows (for the deviation values), the active tiles and the collapse state — so
    // tiles, filters and view settings apply there too.
    backlog,
    weeks,
    ruleRows,
    collapsedCategoryKeys,
    openWeek,
    onOpenWeek,
    onCloseWeek,
    onPeriodChange,
    // Waiting for the activity list is loading too: the request is held until it lands, and a disabled
    // query reports neither loading nor data — which would flash the "nothing deviates" state.
    isLoading: isLoading || isAwaitingActivityOptions,
    isError,
    refetch
  };
};
